import { useStreamingState } from "./useStreamingState";
import { useMessageSync } from "./useMessageSync";
import type { Message } from '@generated/proto/model'

interface MessageRow {
  id: string
  proto: Message
  created?: Date
  modified?: Date
}

// Re-export types for backward compatibility
export interface StreamingMessage {
  messageId: string
  content: string
  isComplete: boolean
  created: Date
}

/**
 * Hook to manage real-time streaming messages that integrates with Firestore messages.
 * This provides immediate UI updates during streaming while maintaining consistency with Firestore.
 * 
 * Refactored to use a clean architecture with separated concerns:
 * - useStreamingState: Simple CRUD operations for streaming messages
 * - useMessageSync: Handle transitions between streaming and Firestore
 * - messageMerger: Pure function for merging logic (used by useMessageSync)
 * 
 * Key improvements:
 * - Fixed jumping behavior by using synchronous cleanup instead of queueMicrotask
 * - Reduced complexity from 270 lines to ~40 lines
 * - Better separation of concerns and testability
 * - Maintained backward compatibility with existing API
 */
export function useStreamingMessages(firestoreMessages: MessageRow[]) {
  // Simple state management for streaming messages
  const { streamingMessages, actions } = useStreamingState()
  
  // Handle synchronization between streaming and Firestore (fixes jumping issue)
  const { messages } = useMessageSync(
    firestoreMessages,
    streamingMessages,
    actions.clearMultiple
  )

  // Return the same API as before for backward compatibility
  return {
    messages,
    createPlaceholder: actions.createPlaceholder,
    updateStreamingContent: actions.updateStreamingContent,
    completeStreaming: actions.completeStreaming,
    clearStreaming: actions.clearStreaming
  }
}